import DiagnosticsModule, {
  MetricNames,
  MetricTags,
} from '@modules/DiagnosticsModule';
import {
  RTCSMetricNames,
  RTCSMetricTags,
  RTCSOperationNames,
  RTCSStatus,
  RtcsOutageOutcome,
} from '../../metrics/rtcsMetrics';
import { RtcsClient } from '@servicetitan/telecom-rtcs-client';

/**
 * Connection State Monitor
 *
 * Tracks RTCS (Real-Time Communication Service) connection state transitions and emits metrics.
 * Polls every 5 seconds to detect state changes (connected ↔ disconnected ↔ connecting).
 *
 * Metrics emitted:
 * - rtcs_state_change_total: Counter for each state transition
 * - latency_duration: How long the connection stayed in each state
 * - rtcs_disconnect_duration: How long an outage lasts, measured from the first
 *   poll that sees the client leave connected (typically reconnecting). Tagged
 *   recovered when the client reconnects, lost when it gives up.
 *
 * Error handling: Metrics collection failures are caught and logged to prevent
 * disrupting connection monitoring (metrics are non-critical).
 */

let lastKnownState: string | null = null;
let stateChangeTime: number | null = null;
let outageStartTime: number | null = null;
let monitoringIntervalId: ReturnType<typeof setInterval> | null = null;
let monitoredClient: RtcsClient | null = null;

function resetTracking(): void {
  lastKnownState = null;
  stateChangeTime = null;
  outageStartTime = null;
}

function isOutageState(state: string): boolean {
  return (
    state === RTCSStatus.RECONNECTING ||
    state === RTCSStatus.DISCONNECTED ||
    state === RTCSStatus.FAILED
  );
}

/**
 * Initializes state tracking on first poll.
 */
function initializeStateTracking(currentState: string): void {
  lastKnownState = currentState;
  stateChangeTime = Date.now();
  if (isOutageState(currentState)) {
    outageStartTime = Date.now();
  }
}

/**
 * Emits metrics for state transitions.
 */
function emitStateChangeMetrics(
  fromState: string,
  toState: string,
  duration: number,
): void {
  DiagnosticsModule.incrementCounter(RTCSMetricNames.RTCS_STATE_CHANGE_TOTAL, {
    [RTCSMetricTags.RTCS_STATE_FROM]: fromState,
    [RTCSMetricTags.RTCS_STATE_TO]: toState,
  });

  DiagnosticsModule.recordHistogram(MetricNames.LATENCY_DURATION, duration, {
    [MetricTags.OPERATION_NAME]: RTCSOperationNames.RTCS_STATE_DURATION,
    state: fromState,
  });
}

/**
 * Records how long the open outage lasted, tagged with how it ended.
 */
function endOutage(outcome: string): void {
  if (outageStartTime === null) {
    return;
  }

  const outageDuration = Date.now() - outageStartTime;
  outageStartTime = null;
  if (outageDuration < 0) {
    return;
  }

  DiagnosticsModule.recordHistogram(
    MetricNames.LATENCY_DURATION,
    outageDuration,
    {
      [MetricTags.OPERATION_NAME]: RTCSOperationNames.RTCS_DISCONNECT_DURATION,
      [RTCSMetricTags.RTCS_OUTAGE_OUTCOME]: outcome,
    },
  );
}

/**
 * Tracks outage duration. An outage starts when the client leaves connected
 * (SignalR reports reconnecting while retrying) and ends on reconnect or when
 * the client gives up (disconnected/failed after reconnecting).
 */
function handleOutageTracking(fromState: string, currentState: string): void {
  if (currentState === RTCSStatus.CONNECTED) {
    endOutage(RtcsOutageOutcome.RECOVERED);
    return;
  }

  if (outageStartTime === null) {
    if (isOutageState(currentState) && !isOutageState(fromState)) {
      outageStartTime = Date.now();
    }
    return;
  }

  const gaveUp =
    currentState === RTCSStatus.DISCONNECTED ||
    currentState === RTCSStatus.FAILED;
  if (gaveUp && fromState === RTCSStatus.RECONNECTING) {
    endOutage(RtcsOutageOutcome.LOST);
  }
}

/**
 * Handles state changes and emits appropriate metrics.
 */
function handleStateChange(currentState: string): void {
  if (stateChangeTime === null) {
    return;
  }

  const duration = Date.now() - stateChangeTime;
  emitStateChangeMetrics(lastKnownState!, currentState, duration);
  handleOutageTracking(lastKnownState!, currentState);

  lastKnownState = currentState;
  stateChangeTime = Date.now();
}

/**
 * Monitors the RTCS connection state on each interval tick.
 */
function monitorConnectionState(): void {
  if (!monitoredClient) {
    return;
  }

  const currentState = monitoredClient.state;

  if (lastKnownState === null) {
    initializeStateTracking(currentState);
    return;
  }

  if (currentState !== lastKnownState) {
    handleStateChange(currentState);
  }
}

/**
 * Starts monitoring the RTCS connection state.
 * Checks state every 5 seconds and emits metrics on state transitions.
 * Called again with a new client (workspace reopen), it rebinds to that client.
 * @param workspaceClient - The RTCS client to monitor, or null to skip monitoring
 */
export function startConnectionStateMonitoring(
  workspaceClient: RtcsClient | null,
): void {
  if (monitoringIntervalId) {
    if (workspaceClient === monitoredClient) {
      return;
    }

    // The old client is dead; an outage still open on it ended in loss.
    endOutage(RtcsOutageOutcome.LOST);
    resetTracking();
    monitoredClient = workspaceClient;
    return;
  }

  monitoredClient = workspaceClient;
  monitoringIntervalId = setInterval(monitorConnectionState, 5000);
}

/**
 * Stops monitoring the RTCS connection state.
 * Used for cleanup during module unload or testing.
 */
export function stopConnectionStateMonitoring(): void {
  if (monitoringIntervalId) {
    clearInterval(monitoringIntervalId);
    monitoringIntervalId = null;
    resetTracking();
    monitoredClient = null;
  }
}
